/*
 * fly-to-cart.js — "add to cart" flight animation.
 * When a product is added to the cart (collection quick-add chip, homepage "Shop by Gear"
 * chip, or the PDP add-to-cart button) a clone of the product image arcs up to the header
 * cart icon, shrinking and fading, then the cart icon gives a little bump.
 *
 * Pure vanilla, no dependencies, delegated listener. Optimistic (fires on click) so the
 * feedback is instant. Respects prefers-reduced-motion and no-ops when it can't resolve a
 * product image or the cart icon.
 */
(function () {
  'use strict';

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Buttons that represent a real "add to cart" (not a variant chooser / disabled control).
  var TRIGGER = [
    '.bst-card__add',
    '.flo__row-btn--add',
    '.quick-add__button--add',
    '.product-form__submit',
    'button[name="add"]',
    '.bbx__add',
    'button.gg__add',
    '.gg__qadd'
  ].join(',');

  function isDisabled(el) {
    return el.disabled || el.getAttribute('aria-disabled') === 'true' || el.classList.contains('is-loading');
  }

  // Walk up from the button to the nearest container that holds a product image, return that img.
  function findProductImage(btn) {
    // Bundle builder: fly from a selected add-on's thumb (falls back to the first tile image).
    var dlg = btn.closest('.bbx__dialog');
    if (dlg) {
      var picked = null;
      try { picked = dlg.querySelector('.bbx__item:has(.bbx__input:checked) .bbx__thumb img'); } catch (e) {}
      picked = picked || dlg.querySelector('.bbx__thumb img');
      if (picked && picked.currentSrc) return picked;
    }
    // Gear grid: the open panel flies its current photo, a tile's quick add the tile's photo.
    var ggPanel = btn.closest('.gg__panel');
    if (ggPanel) {
      var cur = ggPanel.querySelector('.gg__slide.is-current img') || ggPanel.querySelector('.gg__slide img');
      if (cur && cur.currentSrc) return cur;
    }
    var ggItem = btn.closest('.gg__item');
    if (ggItem) {
      var ti = ggItem.querySelector('.gg__tile-img:not(.gg__tile-img--alt)');
      if (ti && ti.currentSrc) return ti;
    }
    var scopes = [
      '.bst-card',
      '.flo__row',
      '.product-card',
      'quick-add-component',
      '.quick-add-modal__content',
      '.product-information',
      'product-form-component',
      'form'
    ];
    for (var i = 0; i < scopes.length; i++) {
      var scope = btn.closest(scopes[i]);
      if (!scope) continue;
      // Prefer a visible, loaded image inside the scope.
      var imgs = scope.querySelectorAll('img');
      for (var j = 0; j < imgs.length; j++) {
        var im = imgs[j];
        var r = im.getBoundingClientRect();
        if (r.width > 24 && r.height > 24 && im.currentSrc) return im;
      }
    }
    return null;
  }

  function isVisible(el) {
    if (!el) return false;
    var r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return false;
    var cs = getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.display === 'none' || parseFloat(cs.opacity) === 0) return false;
    // Must be roughly on-screen (sticky headers clone the cart; the hidden copy sits off-screen
    // or at 0×0 and would send the clone flying to the top-left corner instead of the cart).
    return r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
  }

  // Return the *visible* header cart icon (there can be several copies in a sticky header,
  // plus hidden drawer shortcuts). The live header uses <cart-icon class="header-actions__cart-icon">.
  function getCartTarget() {
    var candidates = document.querySelectorAll(
      '.header-actions__cart-icon, cart-icon, .action__cart, cart-icon-component, .header-actions__action[href$="/cart"], [href$="/cart"]'
    );
    for (var i = 0; i < candidates.length; i++) {
      if (isVisible(candidates[i])) return candidates[i];
    }
    return null;
  }

  function bumpCart(target) {
    var el = target.querySelector('.svg-wrapper, svg, cart-icon-component') || target;
    el.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.28)' },
        { transform: 'scale(0.94)' },
        { transform: 'scale(1)' }
      ],
      { duration: 460, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
    );
  }

  function fly(img, target, btn) {
    var src = img.getBoundingClientRect();
    var seen = Math.min(src.bottom, window.innerHeight) - Math.max(src.top, 0);
    if (btn && seen < src.height * 0.4) {
      // the photo is (mostly) off screen: start a photo-sized-down square from the button instead
      var b = btn.getBoundingClientRect(), side = Math.min(96, b.height * 1.6);
      src = { left: b.left + b.width / 2 - side / 2, top: b.top + b.height / 2 - side / 2, width: side, height: side };
    }
    var dst = target.getBoundingClientRect();

    // Clone as a fixed-position element on top of everything, unclipped by card overflow.
    var clone = document.createElement('img');
    clone.src = img.currentSrc || img.src;
    clone.setAttribute('aria-hidden', 'true');
    var s = clone.style;
    s.position = 'fixed';
    s.left = src.left + 'px';
    s.top = src.top + 'px';
    s.width = src.width + 'px';
    s.height = src.height + 'px';
    // same fit as the source (a contained studio shot would be cropped/zoomed by cover)
    var fit = getComputedStyle(img).objectFit;
    s.objectFit = fit === 'contain' ? 'contain' : 'cover';
    if (fit === 'contain') s.background = '#f0f0f0';
    s.borderRadius = '14px';
    s.zIndex = '2147483000';
    s.pointerEvents = 'none';
    s.boxShadow = '0 12px 34px rgba(0,0,0,0.28)';
    s.willChange = 'transform, opacity';
    document.body.appendChild(clone);

    // Deltas from source top-left to the cart icon centre.
    var dx = (dst.left + dst.width / 2) - (src.left + src.width / 2);
    var dy = (dst.top + dst.height / 2) - (src.top + src.height / 2);
    // Arc: rise before curving toward the cart.
    var arc = Math.min(140, Math.abs(dy) * 0.4 + 60);

    var anim = clone.animate(
      [
        { transform: 'translate(0px, 0px) scale(1)', opacity: 1, offset: 0 },
        { transform: 'translate(' + dx * 0.5 + 'px, ' + (dy * 0.5 - arc) + 'px) scale(0.55)', opacity: 0.95, offset: 0.55 },
        { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(0.12)', opacity: 0.2, offset: 1 }
      ],
      { duration: 780, easing: 'cubic-bezier(0.5, 0, 0.75, 0.35)', fill: 'forwards' }
    );

    anim.onfinish = function () {
      clone.remove();
      bumpCart(target);
    };
    anim.oncancel = function () { clone.remove(); };
  }

  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest ? e.target.closest(TRIGGER) : null;
      if (!btn) return;
      if (btn.classList.contains('quick-add__button--choose')) return; // opens a variant picker, no add yet
      if (btn.hasAttribute('data-fly-skip')) return; // e.g. the ATC relabeled to "Build your bundle" — it opens a modal, not an add
      if (isDisabled(btn)) return;

      var target = getCartTarget();
      if (!target) return;
      var img = findProductImage(btn);
      if (!img) return;

      fly(img, target, btn);
    },
    true
  );
})();
